<script lang="ts" setup>
import { onMounted, ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';

import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';

import { useSalesRepStore } from '@/store/Admin/Tenant/SalesRep';
import { useTenantStore } from '@/store';

import { CreateSalesRepInterface, TenantUsersInterface } from '@/types';
import { EMIT } from '@/enum';

const emit = defineEmits([EMIT.CANCEL, EMIT.SAVE, EMIT.CLOSE]);
const visible = ref(true);
const mode = ref<'manual' | 'select'>('manual');
const salesRepStore = useSalesRepStore();
const tenantStore = useTenantStore();
const firstName = ref('');
const lastName = ref('');
const selectedUser = ref<TenantUsersInterface | null>(null);
const availableUsers = computed(() => tenantStore.userDropdown);
const errors = ref<{ firstName?: string; lastName?: string; selectedUser?: string }>({});
const route = useRoute();
const id = ref<number>();

function splitDisplayName(displayName: string): { first_name: string; last_name: string } {
  const parts = displayName.trim().split(' ');
  const first_name = parts[0];
  const last_name = parts.slice(1).join(' ') || '';
  return { first_name, last_name };
}

function validateForm() {
  errors.value = {};

  if (mode.value === 'manual') {
    if (!firstName.value) errors.value.firstName = 'First name is required.';
    if (!lastName.value) errors.value.lastName = 'Last name is required.';
    return !errors.value.firstName && !errors.value.lastName;
  } else {
    if (!selectedUser.value) errors.value.selectedUser = 'User selection is required.';
    return !!selectedUser.value;
  }
}

async function submit() {
  if (!validateForm()) return;

  const createSalesRep = () => {
    if (mode.value === 'manual') {
      const payload: CreateSalesRepInterface = {
        first_name: firstName.value,
        last_name: lastName.value,
        display_name: `${firstName.value} ${lastName.value}`,
      };
      return salesRepStore.createSalesRep(payload);
    } else if (selectedUser.value) {
      const { first_name, last_name } = splitDisplayName(selectedUser.value.display_name);
      const payload: CreateSalesRepInterface = {
        person_id: selectedUser.value.id,
        display_name: selectedUser.value.display_name,
        first_name,
        last_name,
      };
      return salesRepStore.createSalesRep(payload);
    }
  };

  const result = createSalesRep();
  if (result) {
    result
      .then(() => {
        salesRepStore.doToast('Sales Rep created');
        emit(EMIT.SAVE);
      })
      .catch((error: { status: number; data: { message: string } }) => {
        if (error.status == 422) {
          salesRepStore.doToast(`Failed to save Sales Rep: ${error.data.message}`, '', true);
        } else {
          salesRepStore.doToast(`An unknown error occurred`, '', true);
        }
        emit(EMIT.CLOSE);
      });
  }
}

watch(visible, (val) => {
  if (!val) emit(EMIT.CANCEL);
});

onMounted(async () => {
  if (route.params.id) {
    id.value = parseInt(route.params.id as string);
    await tenantStore.getTenantUsers(id.value);
  }
  id.value = parseInt(route.params.id as string);
});
</script>

<template>
  <Dialog v-model:visible="visible" header="Add Sales Rep" modal style="width: 30rem">
    <template #header>
      <h2>Add Sales Rep</h2>
    </template>
    <div class="mb-4">
      <label class="font-semibold mb-2 block">Select Method</label>
      <div class="flex gap-2">
        <AppButton text="New User" type="primary" :outlined="mode !== 'manual'" @click="mode = 'manual'" />
        <AppButton text="Existing User" type="primary" :outlined="mode !== 'select'" @click="mode = 'select'" />
      </div>
    </div>

    <div v-if="mode === 'manual'" class="space-y-4">
      <div class="field mt-3">
        <label class="required">First Name</label>
        <InputText v-model="firstName" class="w-full" />
        <small v-if="errors.firstName" class="p-error">{{ errors.firstName }}</small>
      </div>
      <div class="field mt-3">
        <label class="required">Last Name</label>
        <InputText v-model="lastName" class="w-full" />
        <small v-if="errors.lastName" class="p-error">{{ errors.lastName }}</small>
      </div>
    </div>

    <div v-else>
      <label class="required">Select User</label>
      <Dropdown
        v-model="selectedUser"
        :options="availableUsers"
        option-label="display_name"
        placeholder="Select a user"
        class="w-full"
      />
      <small v-if="errors.selectedUser" class="p-error">{{ errors.selectedUser }}</small>
    </div>

    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="$emit(EMIT.CANCEL)" />
      <AppButton text="Save" type="primary" @click="submit" />
    </template>
  </Dialog>
</template>
